// Queries y mutations de la integración con máquinas (propuesta
// 2026-10-alimentos-rionegro). Viven aparte de queries.ts para no engordar ese
// módulo; siguen sus mismas convenciones: factory de keys, apiGet + Zod en
// cada lectura y mutations que invalidan por raíz.
//
// Contratos: packages/schemas/src/ingesta_maquina.ts. Rutas del API:
//   completar_tablet.ts, coordinador_ingesta.ts, admin_ingesta.ts.

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  adminIngestaResumenSchema,
  cambiosReferenciaResponseSchema,
  conflictoIngestaSchema,
  conflictosIngestaResponseSchema,
  coordinadorConectividadResponseSchema,
  coordinadorVentanasSinSenalResponseSchema,
  credencialIngestaCreadaSchema,
  credencialIngestaSchema,
  ingestaConfigSchema,
  mapeoIngestaSchema,
  paroPendienteCausalSchema,
  registroPendienteCompletarSchema,
  sobrellenadoResponseSchema,
  usuarioTecnicoIngestaSchema,
  type CambiosReferenciaQuery,
  type CategoriaParoCambioReferenciaUpdate,
  type ConflictoResolver,
  type CredencialIngestaCreate,
  type IngestaConfigUpdate,
  type LineaFuenteCapturaUpdate,
  type MapeoIngestaCreate,
  type ParoCompletarCausal,
  type RegistroCompletar,
  type SkuPesoUpdate,
  type SobrellenadoQuery,
} from 'schemas';
import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import { intervaloCaptura } from './intervalo-vivo';

export type EstadoConflictosFiltro = 'pendiente' | 'resueltos';

export const ingestaKeys = {
  parosPendientesCausal: () => ['ingesta', 'paros-pendientes-causal'] as const,
  registrosPendientesCompletar: () => ['ingesta', 'registros-pendientes-completar'] as const,
  conectividad: () => ['ingesta', 'conectividad'] as const,
  ventanasSinSenal: () => ['ingesta', 'conectividad', 'ventanas'] as const,
  sobrellenado: (q: SobrellenadoQuery) => ['ingesta', 'sobrellenado', q] as const,
  cambiosReferencia: (q: CambiosReferenciaQuery) => ['ingesta', 'cambios-referencia', q] as const,
  conflictos: (estado: EstadoConflictosFiltro) => ['ingesta', 'conflictos', estado] as const,
  adminCredenciales: () => ['ingesta', 'admin', 'credenciales'] as const,
  adminMapeos: () => ['ingesta', 'admin', 'mapeos'] as const,
  adminConfig: () => ['ingesta', 'admin', 'config'] as const,
  adminResumen: () => ['ingesta', 'admin', 'resumen'] as const,
};

const parosPendientesSchema = z.array(paroPendienteCausalSchema);
const registrosPendientesSchema = z.array(registroPendienteCompletarSchema);
const credencialesSchema = z.array(credencialIngestaSchema);
const mapeosSchema = z.array(mapeoIngestaSchema);

// Respuestas de PATCH que el paquete de schemas no declara: se validan solo
// los campos que la UI usa (el resto pasa sin tocar).
const paroCausalCompletadaSchema = z
  .object({ id: z.string().uuid(), pendiente_causal: z.boolean() })
  .passthrough();
const registroCompletadoSchema = z
  .object({ id: z.string().uuid(), pendiente_completar: z.boolean() })
  .passthrough();
const lineaFuenteCapturaSchema = z.object({
  id: z.string().uuid(),
  fuente_captura: z.string(),
  minutos_cambio_referencia_estandar: z.number().nullable(),
});
const skuPesoSchema = z.object({
  id: z.string().uuid(),
  peso_nominal_g: z.number().nullable(),
  tolerancia_g: z.number().nullable(),
  costo_referencia_kg: z.number().nullable(),
});
const categoriaCambioReferenciaSchema = z.object({
  id: z.string().uuid(),
  es_cambio_referencia: z.boolean(),
});

export type LineaFuenteCapturaResultado = z.infer<typeof lineaFuenteCapturaSchema>;
export type SkuPesoResultado = z.infer<typeof skuPesoSchema>;

// ─── Completar desde la tablet (líder / supervisor) ─────────────────────────

/**
 * Paros abiertos por la máquina sin causal, en el alcance del rol. Poll 30 s;
 * 10 s si `fuenteCaptura` (línea del líder) es híbrida o máquina (R12).
 */
export function useParosPendientesCausal(
  opts: { enabled?: boolean; fuenteCaptura?: string | null } = {},
) {
  return useQuery({
    queryKey: ingestaKeys.parosPendientesCausal(),
    queryFn: () => apiGet('/api/paros/pendientes-causal', parosPendientesSchema),
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: intervaloCaptura(opts.fuenteCaptura),
  });
}

/**
 * Horas de máquina que esperan lote/operarios de una persona. Poll 30 s; 10 s
 * si `fuenteCaptura` (línea del líder) es híbrida o máquina (R12).
 */
export function useRegistrosPendientesCompletar(
  opts: { enabled?: boolean; fuenteCaptura?: string | null } = {},
) {
  return useQuery({
    queryKey: ingestaKeys.registrosPendientesCompletar(),
    queryFn: () => apiGet('/api/registros/pendientes-completar', registrosPendientesSchema),
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: intervaloCaptura(opts.fuenteCaptura),
  });
}

export function useCompletarCausalParo() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ paroId, body }: { paroId: string; body: ParoCompletarCausal }) =>
      apiPatch(`/api/paros/${paroId}/causal`, body, paroCausalCompletadaSchema),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ingestaKeys.parosPendientesCausal() });
      void qc.invalidateQueries({ queryKey: ingestaKeys.registrosPendientesCompletar() });
      void qc.invalidateQueries({ queryKey: ['paros', 'abiertos'] });
      void qc.invalidateQueries({ queryKey: ['registros', 'turno-hoy'] });
      void qc.invalidateQueries({ queryKey: ['supervisor', 'dashboard'] });
    },
  });
}

export function useCompletarRegistro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ registroId, body }: { registroId: string; body: RegistroCompletar }) =>
      apiPatch(`/api/registros/${registroId}/completar`, body, registroCompletadoSchema),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ingestaKeys.registrosPendientesCompletar() });
      void qc.invalidateQueries({ queryKey: ['registros', 'turno-hoy'] });
      void qc.invalidateQueries({ queryKey: ['supervisor', 'pendientes'] });
    },
  });
}

// ─── Lecturas del coordinador (y directivo) ─────────────────────────────────

/**
 * Conectividad de las líneas híbridas o de máquina del alcance. Arreglo vacío
 * = el tenant no tiene líneas de máquina (la UI nueva se oculta).
 */
export function useConectividadLineas(opts: { enabled?: boolean; poll?: boolean } = {}) {
  return useQuery({
    queryKey: ingestaKeys.conectividad(),
    queryFn: () => apiGet('/api/coordinador/conectividad', coordinadorConectividadResponseSchema),
    enabled: opts.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: opts.poll ? 30_000 : false,
  });
}

/** Ventanas sin señal de los últimos 7 días (abiertas y cerradas). */
export function useVentanasSinSenal(opts: { poll?: boolean } = {}) {
  return useQuery({
    queryKey: ingestaKeys.ventanasSinSenal(),
    queryFn: () =>
      apiGet('/api/coordinador/conectividad/ventanas', coordinadorVentanasSinSenalResponseSchema),
    staleTime: 30_000,
    refetchInterval: opts.poll ? 30_000 : false,
  });
}

function qsDe(q: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(q)) if (v) sp.set(k, v);
  return sp.toString();
}

export function useSobrellenado(q: SobrellenadoQuery, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ingestaKeys.sobrellenado(q),
    queryFn: () =>
      apiGet(
        `/api/coordinador/sobrellenado?${qsDe({
          desde: q.desde,
          hasta: q.hasta,
          planta_id: q.planta_id,
          linea_id: q.linea_id,
        })}`,
        sobrellenadoResponseSchema,
      ),
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useCambiosReferencia(q: CambiosReferenciaQuery, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ingestaKeys.cambiosReferencia(q),
    queryFn: () =>
      apiGet(
        `/api/coordinador/cambios-referencia?${qsDe({
          desde: q.desde,
          hasta: q.hasta,
          linea_id: q.linea_id,
        })}`,
        cambiosReferenciaResponseSchema,
      ),
    enabled: opts.enabled ?? true,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

export function useConflictosIngesta(estado: EstadoConflictosFiltro) {
  return useQuery({
    queryKey: ingestaKeys.conflictos(estado),
    queryFn: () =>
      apiGet(
        `/api/coordinador/ingesta/conflictos?estado=${estado}`,
        conflictosIngestaResponseSchema,
      ),
    staleTime: 30_000,
    refetchInterval: estado === 'pendiente' ? 30_000 : false,
  });
}

export function useResolverConflicto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: ConflictoResolver }) =>
      apiPatch(`/api/coordinador/ingesta/conflictos/${id}/resolver`, body, conflictoIngestaSchema),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ingesta', 'conflictos'] });
      void qc.invalidateQueries({ queryKey: ['coordinador', 'dashboard'] });
      void qc.invalidateQueries({ queryKey: ['coordinador', 'linea-tiempo'] });
    },
  });
}

// ─── Admin tenant: integración de planta ────────────────────────────────────

export function useCredencialesIngesta() {
  return useQuery({
    queryKey: ingestaKeys.adminCredenciales(),
    queryFn: () => apiGet('/api/admin/ingesta/credenciales', credencialesSchema),
    staleTime: 30_000,
  });
}

/**
 * Crea una credencial. La respuesta trae el secreto completo UNA sola vez.
 * gcTime 0: la mutación sale de la caché apenas el llamador hace reset()
 * (el diálogo guarda el secreto en su estado local y lo descarta al cerrar).
 */
export function useCrearCredencialIngesta() {
  const qc = useQueryClient();
  return useMutation({
    gcTime: 0,
    mutationFn: (body: CredencialIngestaCreate) =>
      apiPost('/api/admin/ingesta/credenciales', body, credencialIngestaCreadaSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: ingestaKeys.adminCredenciales() }),
  });
}

/** Revocar es POST …/:id/revocar (el API no expone DELETE de credenciales). */
export function useRevocarCredencialIngesta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost(`/api/admin/ingesta/credenciales/${id}/revocar`, {}, credencialIngestaSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: ingestaKeys.adminCredenciales() }),
  });
}

/** Crea el usuario técnico o devuelve el vigente (`creado: false`). */
export function useCrearUsuarioTecnico() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost('/api/admin/ingesta/usuario-tecnico', {}, usuarioTecnicoIngestaSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: ingestaKeys.adminConfig() }),
  });
}

export function useMapeosIngesta() {
  return useQuery({
    queryKey: ingestaKeys.adminMapeos(),
    queryFn: () => apiGet('/api/admin/ingesta/mapeos', mapeosSchema),
    staleTime: 30_000,
  });
}

export function useCrearMapeoIngesta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MapeoIngestaCreate) =>
      apiPost('/api/admin/ingesta/mapeos', body, mapeoIngestaSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: ingestaKeys.adminMapeos() }),
  });
}

export function useEliminarMapeoIngesta() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/ingesta/mapeos/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ingestaKeys.adminMapeos() }),
  });
}

export function useIngestaConfig() {
  return useQuery({
    queryKey: ingestaKeys.adminConfig(),
    queryFn: () => apiGet('/api/admin/ingesta/config', ingestaConfigSchema),
    staleTime: 60_000,
  });
}

export function useGuardarIngestaConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: IngestaConfigUpdate) =>
      apiPatch('/api/admin/ingesta/config', body, ingestaConfigSchema),
    onSuccess: (cfg) => qc.setQueryData(ingestaKeys.adminConfig(), cfg),
  });
}

/** Valores vigentes de fuente de captura por línea y de peso por SKU. */
export function useAdminIngestaResumen() {
  return useQuery({
    queryKey: ingestaKeys.adminResumen(),
    queryFn: () => apiGet('/api/admin/ingesta/resumen', adminIngestaResumenSchema),
    staleTime: 60_000,
  });
}

export function useActualizarFuenteCapturaLinea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ lineaId, body }: { lineaId: string; body: LineaFuenteCapturaUpdate }) =>
      apiPatch(`/api/admin/lineas/${lineaId}/fuente-captura`, body, lineaFuenteCapturaSchema),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ingestaKeys.conectividad() });
      void qc.invalidateQueries({ queryKey: ingestaKeys.adminResumen() });
    },
  });
}

export function useActualizarPesoSku() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, body }: { skuId: string; body: SkuPesoUpdate }) =>
      apiPatch(`/api/admin/skus/${skuId}/peso`, body, skuPesoSchema),
    onSuccess: () => qc.invalidateQueries({ queryKey: ingestaKeys.adminResumen() }),
  });
}

export function useMarcarCambioReferenciaCategoria() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: CategoriaParoCambioReferenciaUpdate }) =>
      apiPatch(
        `/api/admin/categorias-paro/${id}/cambio-referencia`,
        body,
        categoriaCambioReferenciaSchema,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'categorias-paro'] }),
  });
}
